import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, CancelCrossIcon, IconButton } from "@activecollab/components";
import { List } from "immutable";
import moment from "moment";
import PropTypes from "prop-types";
import { useChatContext } from "~/components/Chat/context/ChatContext.js";
import { MessageForm } from "./MessageForm.jsx";
import { makeGetConversationMessageById } from "../../../../store/selectors/selector.conversationMessages.jsx";
import { useWhoIsWriting } from "../../../Hooks/index.jsx";

export const Footer = ({ conversationId, onSubmit, editMessageId, onCancel, onStartEditLastMessage }) => {
  const { draft, setDraft } = useChatContext();
  const { stopWriting, startWriting } = useWhoIsWriting("message");
  const { t } = useTranslation();

  const editMode = editMessageId > 0;

  const getMessageById = useMemo(() => makeGetConversationMessageById(editMessageId), [editMessageId]);
  const message = useSelector(getMessageById);

  const conversationDraft = typeof draft === "object" ? draft[`${conversationId}`] : {};
  const body = message?.body || (!editMode && conversationDraft?.body) || "";
  const attachmentIds = useMemo(() => {
    return editMode && message?.attachments ? List(message.attachments) : List();
  }, [editMode, message?.attachments]);

  const defaultAttachments = useMemo(() => {
    const conversationDraft = typeof draft === "object" && !editMode ? draft[`${conversationId}`] : {};
    const filesToAttach = conversationDraft?.attachments?.filesToAttach || [];
    const filesToDrop = conversationDraft?.attachments?.filesToDrop || [];
    return {
      filesToAttach,
      filesToDrop,
    };
  }, [conversationId, draft, editMode]);

  useEffect(() => {
    if (editMode) {
      setDraft(({ [`${conversationId}`]: value, ...newDraft }) => newDraft);
    }
  }, [editMode, setDraft, conversationId]);

  const handleSubmitMessage = useCallback(
    (attributes) => {
      stopWriting();
      setDraft(({ [`${conversationId}`]: value, ...newDraft }) => newDraft);
      typeof onSubmit === "function" && onSubmit(attributes);
    },
    [onSubmit, stopWriting, setDraft, conversationId]
  );

  const handleFormLoadPrevious = useCallback(() => {
    !editMode && typeof onStartEditLastMessage === "function" && onStartEditLastMessage();
  }, [editMode, onStartEditLastMessage]);

  const handleCancel = useCallback(
    (event) => {
      event.preventDefault();
      typeof onCancel === "function" && onCancel();
    },
    [onCancel]
  );

  const handleFormChange = useCallback(
    ({ body = "", bodyPlainText = "", attachments = {} }) => {
      const { filesToAttach = [] } = attachments;
      if (body.length > 0 || filesToAttach.length) {
        if (!editMode) {
          setDraft((prevDraft) => ({
            ...prevDraft,
            [`${conversationId}`]: {
              body,
              bodyPlainText,
              attachments,
              timestamp: moment.utc().unix(),
            },
          }));
        }
        startWriting();
      } else {
        setDraft(({ [`${conversationId}`]: value, ...newDraft }) => newDraft);
      }
    },
    [editMode, startWriting, setDraft, conversationId]
  );

  const handleFormCancel = useCallback(() => {
    stopWriting();
    typeof onCancel === "function" && onCancel();
  }, [onCancel, stopWriting]);

  const messageFormProps = {
    conversationId,
    ...defaultAttachments,
    body,
    attachments: defaultAttachments,
    onSubmit: handleSubmitMessage,
    onChange: handleFormChange,
    onCancel: handleFormCancel,
    onLoadPrevious: handleFormLoadPrevious,
    attachmentIds,
  };

  return (
    <div className="hub-footer">
      {editMode ? (
        <div className="edit-message-header tw-py-3 tw-px-4">
          <Body2
            weight="bold"
            color="primary"
          >
            {t("Edit Message")}
          </Body2>
          <IconButton
            variant="text gray"
            size="small"
            onClick={handleCancel}
            className="tw-shrink-0"
          >
            <CancelCrossIcon />
          </IconButton>
        </div>
      ) : null}
      <div className="tw-py-3 tw-px-4">
        {editMode ? (
          <MessageForm
            key="edit-coversation-message"
            {...messageFormProps}
            includeDirtyState
          />
        ) : (
          <MessageForm
            key="add-coversation-message"
            {...messageFormProps}
          />
        )}
      </div>
    </div>
  );
};

Footer.propTypes = {
  conversationId: PropTypes.number.isRequired,
  editMessageId: PropTypes.number,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onStartEditLastMessage: PropTypes.func,
};

Footer.defaultProps = {
  editMessageId: 0,
};
